/**
 * Skill scanner — single source of truth for the file-system layout of
 * provider-native skills, plus Codex's flat custom prompts.
 *
 * Layout owned here:
 *
 *   provider="claude", scope="project" → <workspaceRoot>/.claude/skills/<name>/SKILL.md
 *   provider="claude", scope="global"  → ~/.claude/skills/<name>/SKILL.md
 *   provider="codex",  scope="project" → <workspaceRoot>/.codex/skills/<name>/SKILL.md
 *   provider="codex",  scope="global"  → ~/.codex/skills/<name>/SKILL.md
 *   provider="codex",  scope="codex-prompts" → ~/.codex/prompts/<name>.md   (flat file)
 *   provider="opencode", scope="project" → <workspaceRoot>/.codex/skills/<name>/SKILL.md
 *   provider="opencode", scope="global"  → ~/.codex/skills/<name>/SKILL.md
 *
 * The Skills modal lists/creates/edits files inside these roots ONLY. Any
 * absolute path passed to `writeSkillFrontmatter` is verified to live under
 * one of the roots above before any disk write happens — paths outside this
 * allowlist are rejected. This is the security gate that lets us touch
 * `~/.claude/skills/` from a daemon RPC even though `writeFile` /
 * `exploreFileSystem` are workspace-cwd-scoped by design.
 */

import { execSync } from "node:child_process";
import type { Dirent } from "node:fs";
import fs from "node:fs/promises";
import os from "node:os";
import path from "node:path";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SkillProvider = "claude" | "codex" | "opencode";
export type SkillScope = "project" | "global" | "codex-prompts";

export interface SkillEntry {
  /** Stable identifier — `${provider}:${scope}:${name}`. */
  id: string;
  provider: SkillProvider;
  scope: SkillScope;
  /** Folder name (claude/codex skills) or filename without `.md` (codex prompts). */
  name: string;
  /** Absolute path to the SKILL.md (or prompt .md). */
  path: string;
  /** First-pass description from frontmatter. Empty string if missing. */
  description: string;
  /** ISO timestamp of last modification on the SKILL.md / prompt file. */
  modifiedAt: string;
  size: number;
}

/** User-editable frontmatter fields. Unknown frontmatter fields are preserved by `writeSkillFrontmatter`. */
export interface SkillFrontmatter {
  description?: string;
  argumentHint?: string;
  allowedTools?: string[];
}

export interface ListSkillsArgs {
  provider: SkillProvider;
  scope: SkillScope;
  workspaceRoot?: string;
}

export interface CreateSkillArgs {
  provider: SkillProvider;
  scope: SkillScope;
  workspaceRoot?: string;
  /** Kebab-safe name. No path separators, no leading dots. */
  name: string;
}

export interface CreateSkillResult {
  /** Absolute path to the created SKILL.md (or prompt .md). */
  path: string;
}

export interface WriteSkillFrontmatterArgs {
  /** Absolute path. Must resolve inside an allowlisted skill root. */
  path: string;
  frontmatter: SkillFrontmatter;
}

// ---------------------------------------------------------------------------
// Path resolution
// ---------------------------------------------------------------------------

const NAME_REGEX = /^[a-z0-9][a-z0-9._-]*$/i;

function homeDir(): string {
  return process.env.HOME || os.homedir();
}

/** Mirrors `resolveCodexHomeDir` from codex-app-server-agent.ts (env var override → ~/.codex). */
function codexHomeDir(): string {
  return process.env.CODEX_HOME || path.join(homeDir(), ".codex");
}

function skillsDotDir(provider: SkillProvider): ".claude" | ".codex" {
  return provider === "claude" ? ".claude" : ".codex";
}

/** Returns the directory that holds the per-skill folders (or flat prompt files). */
function resolveScopeDir(
  provider: SkillProvider,
  scope: SkillScope,
  workspaceRoot?: string,
): string {
  if (scope === "codex-prompts") {
    if (provider !== "codex") {
      throw new Error(`Scope "codex-prompts" is only valid for provider "codex"`);
    }
    return path.join(codexHomeDir(), "prompts");
  }
  if (scope === "project") {
    if (!workspaceRoot) {
      throw new Error(`workspaceRoot is required for scope "project"`);
    }
    return path.join(workspaceRoot, skillsDotDir(provider), "skills");
  }
  // scope === "global"
  if (provider === "claude") {
    return path.join(homeDir(), ".claude", "skills");
  }
  return path.join(codexHomeDir(), "skills");
}

/**
 * Returns every absolute directory that may legitimately contain skill files
 * for the given workspace. Used to gate `writeSkillFrontmatter` so we never
 * touch a path outside the allowlist.
 */
function allowedRoots(workspaceRoot?: string): string[] {
  const roots: string[] = [
    path.join(homeDir(), ".claude", "skills"),
    path.join(codexHomeDir(), "skills"),
    path.join(codexHomeDir(), "prompts"),
  ];
  if (workspaceRoot) {
    roots.push(path.join(workspaceRoot, ".claude", "skills"));
    roots.push(path.join(workspaceRoot, ".codex", "skills"));
  }
  return roots.map((r) => path.resolve(r));
}

function isInsideAllowedRoot(absPath: string, workspaceRoot?: string): boolean {
  const resolved = path.resolve(absPath);
  for (const root of allowedRoots(workspaceRoot)) {
    const rel = path.relative(root, resolved);
    if (rel === "" || (!rel.startsWith("..") && !path.isAbsolute(rel))) {
      return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Frontmatter parser/emitter — narrow YAML, preserves unknown keys verbatim
// ---------------------------------------------------------------------------

interface ParsedSkillFile {
  /** Raw lines INSIDE the frontmatter block, in original order. Empty if no frontmatter. */
  rawFrontmatterLines: string[];
  /** The body after the frontmatter (or full file if no frontmatter). */
  body: string;
  /** True if the source file had a `---` block at the top. */
  hadFrontmatter: boolean;
}

const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/;

function parseSkillFile(text: string): ParsedSkillFile {
  const match = FRONTMATTER_RE.exec(text);
  if (!match) {
    return { rawFrontmatterLines: [], body: text, hadFrontmatter: false };
  }
  const yamlBlock = match[1] ?? "";
  const body = match[2] ?? "";
  return {
    rawFrontmatterLines: yamlBlock.split(/\r?\n/),
    body,
    hadFrontmatter: true,
  };
}

/** Read just the description from frontmatter for the list-row preview. */
function readDescription(text: string): string {
  const parsed = parseSkillFile(text);
  if (!parsed.hadFrontmatter) return "";
  for (const line of parsed.rawFrontmatterLines) {
    const m = /^description\s*:\s*(.*)$/.exec(line);
    if (m) {
      const value = (m[1] ?? "").trim();
      // strip surrounding quotes
      if (
        (value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))
      ) {
        return value.slice(1, -1);
      }
      return value;
    }
  }
  return "";
}

/**
 * Identify which raw frontmatter lines are part of one of the keys we own
 * (description, argument-hint, allowed-tools). Returns a list of "spans" we
 * can replace; everything else (including any legacy `when_to_use`) is
 * preserved verbatim.
 */
interface FrontmatterSpan {
  key: "description" | "argument-hint" | "allowed-tools";
  startLine: number;
  /** Exclusive — last index belonging to this span. */
  endLine: number;
}

function findOwnedSpans(lines: string[]): FrontmatterSpan[] {
  const spans: FrontmatterSpan[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const keyMatch = /^([A-Za-z][A-Za-z0-9_-]*)\s*:\s*(.*)$/.exec(line);
    if (!keyMatch) {
      i++;
      continue;
    }
    const key = keyMatch[1];
    const value = keyMatch[2];
    if (key !== "description" && key !== "argument-hint" && key !== "allowed-tools") {
      i++;
      continue;
    }
    // Determine the span. If `value` is empty AND the next lines start with
    // `  - ` (list continuation), include them.
    let end = i + 1;
    if (value === "") {
      while (end < lines.length) {
        const next = lines[end] ?? "";
        if (/^\s+-\s+/.test(next) || /^\s*$/.test(next)) {
          end++;
        } else {
          break;
        }
      }
    }
    spans.push({ key, startLine: i, endLine: end });
    i = end;
  }
  return spans;
}

function emitFrontmatterValue(
  key: "description" | "argument-hint" | "allowed-tools",
  value: string | string[],
): string[] {
  if (key === "allowed-tools") {
    if (!Array.isArray(value) || value.length === 0) return [];
    return [`allowed-tools:`, ...value.map((v) => `  - ${v}`)];
  }
  // string key
  const text = typeof value === "string" ? value : "";
  if (text.length === 0) return [];
  // Quote if the value contains chars that complicate parsing.
  const needsQuote = /[:#\n]/.test(text) || text.startsWith(" ") || text.endsWith(" ");
  const formatted = needsQuote ? `"${text.replace(/"/g, '\\"')}"` : text;
  return [`${key}: ${formatted}`];
}

/** Build the new frontmatter line-array, replacing owned spans + preserving everything else. */
function rewriteFrontmatter(originalLines: string[], next: SkillFrontmatter): string[] {
  const spans = findOwnedSpans(originalLines);
  const ownedKeys = new Set(spans.map((s) => s.key));

  // Build a map of replacement lines per owned key. Empty values mean "delete the key".
  const replacements = new Map<FrontmatterSpan["key"], string[]>();
  if (next.description !== undefined) {
    replacements.set("description", emitFrontmatterValue("description", next.description));
  }
  if (next.argumentHint !== undefined) {
    replacements.set("argument-hint", emitFrontmatterValue("argument-hint", next.argumentHint));
  }
  if (next.allowedTools !== undefined) {
    replacements.set("allowed-tools", emitFrontmatterValue("allowed-tools", next.allowedTools));
  }

  // Walk original lines; substitute owned spans with replacement lines (or delete them
  // if the replacement is an empty array — meaning the user cleared that field).
  const out: string[] = [];
  let i = 0;
  while (i < originalLines.length) {
    const span = spans.find((s) => s.startLine === i);
    if (span) {
      if (replacements.has(span.key)) {
        out.push(...(replacements.get(span.key) ?? []));
      } else {
        // Caller didn't touch this key — preserve the original span verbatim.
        for (let j = span.startLine; j < span.endLine; j++) {
          out.push(originalLines[j] ?? "");
        }
      }
      i = span.endLine;
      continue;
    }
    out.push(originalLines[i] ?? "");
    i++;
  }

  // Append any keys the caller set that weren't already present in the original.
  for (const key of ["description", "argument-hint", "allowed-tools"] as const) {
    if (replacements.has(key) && !ownedKeys.has(key)) {
      out.push(...(replacements.get(key) ?? []));
    }
  }

  return out;
}

// ---------------------------------------------------------------------------
// listSkills
// ---------------------------------------------------------------------------

export async function listSkills(args: ListSkillsArgs): Promise<SkillEntry[]> {
  const dir = resolveScopeDir(args.provider, args.scope, args.workspaceRoot);

  let entries: Dirent[];
  try {
    entries = await fs.readdir(dir, { withFileTypes: true });
  } catch {
    return [];
  }

  const results: SkillEntry[] = [];

  if (args.scope === "codex-prompts") {
    // Flat .md files
    for (const entry of entries) {
      if (!entry.isFile()) continue;
      if (!entry.name.endsWith(".md")) continue;
      const name = entry.name.slice(0, -".md".length);
      if (!name) continue;
      const fullPath = path.join(dir, entry.name);
      const stat = await safeStat(fullPath);
      if (!stat) continue;
      const description = await readDescriptionSafely(fullPath);
      results.push({
        id: `${args.provider}:${args.scope}:${name}`,
        provider: args.provider,
        scope: args.scope,
        name,
        path: fullPath,
        description,
        modifiedAt: stat.mtime.toISOString(),
        size: stat.size,
      });
    }
  } else {
    // Folder per skill, SKILL.md inside
    for (const entry of entries) {
      if (!entry.isDirectory() && !entry.isSymbolicLink()) continue;
      const skillDir = path.join(dir, entry.name);
      const skillPath = path.join(skillDir, "SKILL.md");
      const stat = await safeStat(skillPath);
      if (!stat) continue;
      const description = await readDescriptionSafely(skillPath);
      results.push({
        id: `${args.provider}:${args.scope}:${entry.name}`,
        provider: args.provider,
        scope: args.scope,
        name: entry.name,
        path: skillPath,
        description,
        modifiedAt: stat.mtime.toISOString(),
        size: stat.size,
      });
    }
  }

  results.sort((a, b) => a.name.localeCompare(b.name));
  return results;
}

async function safeStat(filePath: string): Promise<{ mtime: Date; size: number } | null> {
  try {
    const s = await fs.stat(filePath);
    return { mtime: s.mtime, size: s.size };
  } catch {
    return null;
  }
}

async function readDescriptionSafely(filePath: string): Promise<string> {
  try {
    const text = await fs.readFile(filePath, "utf8");
    return readDescription(text);
  } catch {
    return "";
  }
}

// ---------------------------------------------------------------------------
// createSkill
// ---------------------------------------------------------------------------

export async function createSkill(args: CreateSkillArgs): Promise<CreateSkillResult> {
  if (!NAME_REGEX.test(args.name)) {
    throw new Error(
      `Invalid skill name "${args.name}". Use letters, digits, dot, underscore, dash.`,
    );
  }
  if (args.name.includes("/") || args.name.includes("\\") || args.name.includes("..")) {
    throw new Error(`Skill name must not contain path separators or "..".`);
  }

  const dir = resolveScopeDir(args.provider, args.scope, args.workspaceRoot);
  await fs.mkdir(dir, { recursive: true });

  if (args.scope === "codex-prompts") {
    const filePath = path.join(dir, `${args.name}.md`);
    try {
      await fs.access(filePath);
      throw new Error(`A prompt named "${args.name}" already exists at ${filePath}`);
    } catch (err: unknown) {
      // ENOENT is what we want — anything else (including the "already exists" we just threw) re-throws.
      if (
        err &&
        typeof err === "object" &&
        "code" in err &&
        (err as { code: string }).code !== "ENOENT"
      ) {
        throw err;
      }
      if (err instanceof Error && err.message.includes("already exists")) {
        throw err;
      }
    }
    const initial = buildStarterPrompt(args.name);
    await fs.writeFile(filePath, initial, "utf8");
    return { path: filePath };
  }

  // Folder skill (claude or codex)
  const skillDir = path.join(dir, args.name);
  let dirExists = false;
  try {
    const stat = await fs.stat(skillDir);
    dirExists = stat.isDirectory();
  } catch {
    dirExists = false;
  }
  if (dirExists) {
    throw new Error(`A skill named "${args.name}" already exists at ${skillDir}`);
  }
  await fs.mkdir(skillDir, { recursive: true });
  const filePath = path.join(skillDir, "SKILL.md");
  const initial = buildStarterSkill(args.name);
  await fs.writeFile(filePath, initial, "utf8");
  return { path: filePath };
}

function buildStarterSkill(name: string): string {
  return `---
name: ${name}
description: ""
---

# ${name}

Describe when this skill should be used and what it does. The body of this
file is loaded into the agent's context when the skill is invoked.
`;
}

function buildStarterPrompt(name: string): string {
  return `---
description: ""
argument-hint: ""
---

# ${name}

Body of the prompt. Use \`$1\`, \`$2\`, ... or \`$ARGUMENTS\` for parameter expansion.
`;
}

// ---------------------------------------------------------------------------
// writeSkillFrontmatter
// ---------------------------------------------------------------------------

export async function writeSkillFrontmatter(
  args: WriteSkillFrontmatterArgs,
  workspaceRoot?: string,
): Promise<void> {
  if (!path.isAbsolute(args.path)) {
    throw new Error(`writeSkillFrontmatter expects an absolute path; got "${args.path}"`);
  }
  if (!isInsideAllowedRoot(args.path, workspaceRoot)) {
    throw new Error(`Path "${args.path}" is not inside an allowlisted skill root`);
  }

  let original: string;
  try {
    original = await fs.readFile(args.path, "utf8");
  } catch (err) {
    throw new Error(
      `Failed to read skill file: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  const parsed = parseSkillFile(original);
  const newLines = rewriteFrontmatter(parsed.rawFrontmatterLines, args.frontmatter);
  const nextFrontmatter = ["---", ...newLines, "---"].join("\n");
  // Preserve original body's leading newline pattern: parseSkillFile drops the
  // single newline between frontmatter and body, so we add exactly one back.
  const nextContent = parsed.hadFrontmatter
    ? `${nextFrontmatter}\n${parsed.body}`
    : `${nextFrontmatter}\n\n${original}`;

  await fs.writeFile(args.path, nextContent, "utf8");
}

/**
 * Probe to support `git rev-parse --show-toplevel` lookups in the same way
 * the codex agent does. Currently unused by the modal, kept for parity if a
 * future "Project (repo root)" tab is added.
 */
export function probeGitRoot(cwd: string): string | null {
  try {
    const output = execSync("git rev-parse --show-toplevel", {
      cwd,
      encoding: "utf8",
      stdio: ["ignore", "pipe", "ignore"],
    });
    const trimmed = output.trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}
